import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { loadLensJsonProperties } from '@/lib/lens-json-properties';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Brain, Home, MessageSquare, Search, X } from 'lucide-react';
import { Seo } from '@/components/seo/Seo';
import { Header } from '@/components/Header';
import { getIndustryAnchorId, groupByIndustry } from '@/lib/industries';
import { Footer } from '@/components/Footer';
import { isExpertBoardConversation } from '@/lib/conversation-methods';
import { isConversationListVisibleMessage } from '@/lib/conversation-list-visibility';
import { getWizardMatchMessage, resolveWizardMatchParam } from '@/lib/wizard-industry-redirect';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';

// Columns the Lenses page reads from thinking_methods rows. Everything else
// (header, teaser, skills, industry) is merged from JSON_PROPERTIES.
const THINKING_METHOD_LENSES_PROJECTION =
  'id,name,author,bio,icon_url,order_index,produces,score,status,author_email';

interface ThinkingMethod {
  id: string;
  name: string;
  author: string;
  bio: string;
  icon_url?: string;
  order_index: number;
  produces?: string[] | null;
  score: number;
  status: string;
  author_email?: string | null;
  industry?: string;
  header?: string;
  teaser?: string;
  skills?: string[];
}

interface Conversation {
  id: string;
  thinking_method_id: string;
  openai_conversation_id?: string | null;
  created_at: string;
  updated_at: string;
  thinking_methods: {
    id: string;
    name: string;
    author: string;
    bio: string;
    icon_url?: string;
  };
  preview?: string | null;
}

type ConversationWithMethod = Omit<Conversation, 'preview'>;

interface Profile {
  is_admin: boolean;
  email?: string;
}

type ConversationListMessageRow = {
  conversation_id: string;
  content: string;
  is_user: boolean;
  properties?: unknown;
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const Lenses = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [thinkingMethods, setThinkingMethods] = useState<ThinkingMethod[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [matchBannerDismissed, setMatchBannerDismissed] = useState(false);
  const [search, setSearch] = useState('');

  const wizardMatchParam = useMemo(() => {
    return resolveWizardMatchParam(location.search, location.hash);
  }, [location.search, location.hash]);

  const matchBanner = useMemo(() => {
    return getWizardMatchMessage(wizardMatchParam);
  }, [wizardMatchParam]);

  useEffect(() => {
    setMatchBannerDismissed(false);
  }, [location.search, location.hash]);

  useEffect(() => {
    // Public page — anonymous users browse freely.
    if (!authLoading) {
      fetchData();
    }
  }, [user, authLoading]);

  // Scroll to industry section from hash
  useEffect(() => {
    if (!loading && location.hash) {
      const id = `industry-${location.hash.slice(1)}`;
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [loading, location.hash]);

  const fetchData = async () => {
    try {
      const { data: methodsData, error: methodsError } = await supabase
        .from('thinking_methods')
        .select(THINKING_METHOD_LENSES_PROJECTION)
        .order('order_index', { ascending: true });

      if (methodsError) throw methodsError;

      let conversationsData: ConversationWithMethod[] = [];
      if (user) {
        const { data, error } = await supabase
          .from('conversations')
          .select(`
            id,
            thinking_method_id,
            openai_conversation_id,
            created_at,
            updated_at,
            thinking_methods (
              id,
              name,
              author,
              bio,
              icon_url
            )
          `)
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(80);

        if (error) throw error;
        conversationsData = (data || []) as unknown as ConversationWithMethod[];
      }

      let profileData: Profile | null = null;
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin, email')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;
        profileData = data;
      }

      const filteredMethods = (methodsData || []).filter((method: ThinkingMethod) => {
        const isAdmin = profileData?.is_admin || false;
        const userEmail = profileData?.email || '';
        
        switch (method.status) {
          case 'draft':
            return false;
          case 'admins':
            return isAdmin;
          case 'author':
            return isAdmin || (method.author_email && method.author_email.toLowerCase() === userEmail.toLowerCase());
          case 'published':
            return true;
          default:
            return false;
        }
      });

      const methodIds = filteredMethods.map((m: ThinkingMethod) => m.id);
      const { data: attrsData } = await loadLensJsonProperties(methodIds);

      const propsMap: Record<string, { industry?: string; header?: string; teaser?: string; skills?: string[] }> = {};
      if (attrsData) {
        for (const attr of attrsData) {
          try {
            const parsed = JSON.parse(attr.attribute_value);
            propsMap[attr.thinking_method_id] = {
              industry: parsed.industry || undefined,
              header: parsed.header || undefined,
              teaser: parsed.teaser || undefined,
              skills: Array.isArray(parsed.skills) ? parsed.skills : undefined,
            };
          } catch (error) {
            console.warn('Ignoring invalid lens JSON properties:', error);
          }
        }
      }

      const methodsWithProps = filteredMethods.map((m: ThinkingMethod) => ({
        ...m,
        ...propsMap[m.id],
      }));

      setThinkingMethods(methodsWithProps);
      
      const visibleMethodIds = new Set(filteredMethods.map(m => m.id));
      const candidateConvs = (conversationsData || [])
        .filter((conversation) => visibleMethodIds.has(conversation.thinking_method_id))
        .filter((conversation) => !isExpertBoardConversation(conversation));

      const visibleConversationIds = new Set<string>();
      const userPreviewMap = new Map<string, string>();
      const fallbackPreviewMap = new Map<string, string>();
      if (candidateConvs.length > 0) {
        const convIds = candidateConvs.map((conversation) => conversation.id);
        const { data: messages } = await supabase
          .from('messages_vr')
          .select('conversation_id, content, is_user, properties')
          .in('conversation_id', convIds)
          .order('created_at', { ascending: true });

        (messages as ConversationListMessageRow[] | null)?.forEach(m => {
          if (!isConversationListVisibleMessage(m)) return;

          visibleConversationIds.add(m.conversation_id);
          if (m.is_user && !userPreviewMap.has(m.conversation_id)) {
            userPreviewMap.set(m.conversation_id, m.content);
          }
          if (!fallbackPreviewMap.has(m.conversation_id)) {
            fallbackPreviewMap.set(m.conversation_id, m.content);
          }
        });
      }

      const baseConvs = candidateConvs
        .filter((conversation) => visibleConversationIds.has(conversation.id))
        .slice(0, 5);

      const filteredConvs: Conversation[] = baseConvs.map((conversation) => ({
        id: conversation.id,
        thinking_method_id: conversation.thinking_method_id,
        openai_conversation_id: conversation.openai_conversation_id,
        created_at: conversation.created_at,
        updated_at: conversation.updated_at,
        thinking_methods: conversation.thinking_methods,
        preview: userPreviewMap.get(conversation.id) ?? fallbackPreviewMap.get(conversation.id) ?? null,
      }));

      setConversations(filteredConvs);
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleMethodClick = (methodId: string) => {
    navigate(`/chat/new?method=${methodId}`);
  };

  const searchTerms = useMemo(() => search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean), [search]);
  const filteredMethods = useMemo(() => {
    return thinkingMethods.filter((method) => {
      const text = [method.name, method.author, method.industry, method.header, method.teaser, method.bio, ...(method.skills || [])]
        .filter(Boolean).join(' ').toLocaleLowerCase();
      return searchTerms.every((term) => text.includes(term));
    });
  }, [thinkingMethods, searchTerms]);
  const groupedByIndustry = useMemo(() => {
    return groupByIndustry(filteredMethods);
  }, [filteredMethods]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-lg px-6 text-center space-y-4">
          <Brain className="h-12 w-12 text-primary mx-auto animate-pulse" />
          <p className="text-lg font-semibold text-foreground">
            {matchBanner?.loadingTitle ?? 'Loading experts...'}
          </p>
          {matchBanner?.loadingDescription && (
            <p className="text-sm leading-6 text-muted-foreground">{matchBanner.loadingDescription}</p>
          )}
        </div>
      </div>
    );
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <Seo
        title="Expert Lenses: Apply Expert Methods to Your Startup | Applyt"
        description="Browse Expert Lenses and apply a proven expert method to your own situation to produce strategies, plans, and finished work."
      />
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />

        {/* Page Header */}
        <section className="relative z-10 pt-16 pb-12 px-6 bg-muted/30">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Select the Expert Method for Your Goal
            </h1>
            <div className="mx-auto mt-8 max-w-xl text-left">
              <label htmlFor="lens-search" className="mb-2 block text-sm font-medium">Search expert methods</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  id="lens-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Escape') setSearch(''); }}
                  placeholder="Name, author, industry, or skill"
                  className="h-12 bg-background pl-10 pr-12 [&::-webkit-search-cancel-button]:appearance-none"
                  aria-describedby="lens-search-results"
                />
                {search && (
                  <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1/2 h-10 w-10 -translate-y-1/2" aria-label="Clear search" onClick={() => setSearch('')}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p id="lens-search-results" role="status" className="mt-2 text-sm text-muted-foreground">
                {searchTerms.length > 0
                  ? `${filteredMethods.length} of ${thinkingMethods.length} expert methods`
                  : `${thinkingMethods.length} expert methods`}
              </p>
            </div>
          </div>
        </section>

        <main className="relative z-10">
          {matchBanner && !matchBannerDismissed && (
            <section className="px-6 pb-4 bg-muted/30">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col gap-4 rounded-2xl border border-primary/15 bg-card px-5 py-4 shadow-sm sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-primary">{matchBanner.title}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{matchBanner.description}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 self-end rounded-full sm:self-start"
                    aria-label="Dismiss match message"
                    onClick={() => setMatchBannerDismissed(true)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </section>
          )}

          {/* Recent Conversations */}
          {conversations.length > 0 && searchTerms.length === 0 && (
            <section className="py-12 px-6">
              <div className="max-w-7xl mx-auto">
                <Card className="border-border/50 bg-card">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      Recent Conversations
                    </h3>
                    <div className="space-y-3">
                      {conversations.map((conversation) => (
                        <div
                          key={conversation.id}
                          className="flex items-start justify-between gap-4 p-3 rounded-lg border border-border/50 hover:border-primary/20 transition-colors cursor-pointer"
                          onClick={() => navigate(`/chat/${conversation.id}`)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{conversation.thinking_methods.name}</p>
                            <p className="text-sm text-muted-foreground truncate mt-0.5">
                              {conversation.preview
                                ? conversation.preview.length > 120
                                  ? conversation.preview.slice(0, 120) + '…'
                                  : conversation.preview
                                : 'Empty conversation'}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 mt-1">
                            {timeAgo(conversation.updated_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          )}

          {/* Industry Carousels */}
          <section className="py-12 px-6 bg-muted/30">
            <div className="max-w-7xl mx-auto space-y-14">
              {groupedByIndustry.map(([industry, methods]) => (
                <div key={industry} id={getIndustryAnchorId(industry)} className="scroll-mt-36">
                  <h2
                    className="text-2xl font-bold mb-6 flex items-center gap-3"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    <span style={{ color: '#C4883A' }}>{industry}</span>
                    <span className="text-sm font-normal text-muted-foreground">
                      ({methods.length} {methods.length === 1 ? 'expert' : 'experts'})
                    </span>
                  </h2>

                  <Carousel
                    opts={{ align: 'start', slidesToScroll: 1 }}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-4">
                      {methods.map((method) => (
                        <CarouselItem
                          key={method.id}
                          className="pl-4 basis-full md:basis-1/2 lg:basis-[45%]"
                        >
                          <div
                            className="group border border-border/50 rounded-2xl overflow-hidden transition-all duration-200 hover:border-primary/30 hover:-translate-y-0.5 cursor-pointer flex flex-col h-full"
                            onClick={() => navigate(`/lens/${method.id}`)}
                          >
                            {/* Header */}
                            <div className="px-7 py-5 bg-muted/50 border-b border-border/50 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/20 to-primary/10 border border-border">
                                  {method.icon_url ? (
                                    <img src={method.icon_url} alt={method.author} className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center text-primary font-semibold text-sm">
                                      {getInitials(method.author)}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-semibold text-sm">{method.author}</div>
                                  <div className="text-xs text-muted-foreground">Author, {method.name}</div>
                                </div>
                              </div>
                            </div>

                            {/* Body */}
                            <div className="px-7 py-6 flex-1 flex flex-col">
                              <h3 className="text-[22px] font-bold mb-2 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                                {method.header || method.name}
                              </h3>
                              <p className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-4 flex-1">
                                {method.teaser || method.bio}
                              </p>
                              {method.skills && method.skills.length > 0 && (
                                <div className="flex gap-2 flex-wrap mt-auto">
                                  {method.skills.slice(0, 3).map((skill, index) => (
                                    <span key={index} className="text-xs px-3 py-1.5 rounded-md bg-muted text-muted-foreground font-medium">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* CTA */}
                            <div
                              className="block text-center py-3.5 bg-foreground text-background text-sm font-semibold border-t border-border/50 hover:bg-foreground/90 transition-colors"
                              onClick={(e) => { e.stopPropagation(); navigate(`/lens/${method.id}`); }}
                            >
                              Use This Method
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="-left-4 md:-left-5" />
                    <CarouselNext className="-right-4 md:-right-5" />
                  </Carousel>
                </div>
              ))}

              {thinkingMethods.length > 0 && filteredMethods.length === 0 && (
                <div className="py-16 text-center">
                  <h2 className="text-2xl font-semibold">No matching expert methods</h2>
                  <p className="mt-3 text-muted-foreground">Try another name, author, industry, or skill.</p>
                  <Button variant="outline" className="mt-6" onClick={() => setSearch('')}>Clear search</Button>
                </div>
              )}

              {thinkingMethods.length === 0 && (
                <div className="text-center py-20">
                  <Brain className="h-20 w-20 text-muted-foreground mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-foreground mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    No Experts Available
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Please contact your administrator to add experts.
                  </p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Lenses;
